import { useResourceMapContext } from 'features/Reserve/contexts/ResourceMapContext';
import {
  sanitizeDurationParam,
  sanitizeTimeParam,
} from 'features/Reserve/utils/sanitizeParamUtils';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { buildCanonicalPath, useCanonicalLink } from 'hooks/useCanonicalLink';
import { useTranslation } from 'react-i18next';
import { IPriceTier } from 'types/experience.types';
import { DATE_FORMAT } from 'utils';
import { getSearchParams } from 'utils/getSearchParams';
import useGetEligibleProgram from 'api/brand/useGetEligibleProgram';
import isHyattEligible from 'utils/pointEligibility/isHyattEligible';
import { useGlobalDateLocale } from 'context/DateLocaleContext';
import ResourceGroupLayout from '../../components/ResourceGroupLayout';
import useAvailableDates from '../../hooks/useAvailableDates';
import useResourceBookingSession from '../../hooks/useResourceBookingSession';
import { useSessionTimeOptions } from '../../hooks/useSessionTimeOptions';
import { ResourceGroupPageSkeleton } from '../Skeleton';

type PriceTier = IPriceTier & {
  id: string;
};

export const MappedResourceGroupPage = () => {
  const {
    collectionId,
    date,
    duration,
    firstAvailableDate,
    groupId,
    latest,
    listing,
    resourceGroup,
    sessions,
    setResourceBookingSessionParams,
    time,
  } = useResourceBookingSession();
  const path = buildCanonicalPath({ collectionId, groupId });
  useCanonicalLink(path);
  const { t: translate } = useTranslation();
  const { locale } = useGlobalDateLocale();
  const allSessionTimes = useSessionTimeOptions(sessions, locale);
  const { focusResources, selectResource, selectedResource, unselectResource } =
    useResourceMapContext();
  const selectedResourcePriceTier = useMemo(
    () =>
      firstAvailableDate?.resourceGroups.find((g) => g.id === groupId)
        ?.firstAvailableDate?.sessions[0]?.priceTiers?.[0],
    [firstAvailableDate, groupId],
  );
  const [dateForCalendarAvailability, setDateForCalendarAvailability] =
    useState<moment.Moment>(date ? moment(date) : moment());

  const { data: eligibleProgram } = useGetEligibleProgram(
    listing?.brandId,
    listing?.id && listing?.brandId && listing?.productLine
      ? {
          listings: [
            {
              id: listing.id,
              brandId: listing.brandId,
              productLine: listing.productLine,
            },
          ],
        }
      : null,
  );

  useEffect(() => {
    focusResources([]);
  }, []);

  useEffect(() => {
    if (
      date &&
      duration &&
      selectedResource &&
      time &&
      !sessions.find(
        (session) =>
          session.experienceId === selectedResource &&
          session.startDateTime === `${date}T${time}` &&
          session.duration === duration,
      )
    ) {
      unselectResource();
    }
  }, [sessions, date, time, duration, unselectResource, selectedResource]);

  useEffect(() => {
    if (date) {
      setDateForCalendarAvailability(moment(date));
    }
  }, [date]);

  const { availableDates, isLoadingAvailableDates } = useAvailableDates({
    from: dateForCalendarAvailability
      .clone()
      .startOf('month')
      .format(DATE_FORMAT),
    to: dateForCalendarAvailability.clone().endOf('month').format(DATE_FORMAT),
    collectionId: listing?.resourceGroupCollectionId,
    resourceGroupId: groupId,
    latest,
  });

  const handleReserveClick = useCallback(() => {
    const { time: latestTime, duration: latestDuration } = getSearchParams([
      'time',
      'duration',
    ]);
    if (selectedResource) {
      setResourceBookingSessionParams({
        resourceId: selectedResource,
        groupId,
        time: sanitizeTimeParam(
          Array.isArray(latestTime) ? latestTime[0] : latestTime,
        ),
        duration: sanitizeDurationParam(
          Array.isArray(latestDuration) ? latestDuration[0] : latestDuration,
        ),
      });
    }
  }, [selectedResource, groupId]);

  const handleSessionChange = useCallback(
    (time: string, duration: number) => {
      setResourceBookingSessionParams({
        time,
        duration,
      });
    },
    [setResourceBookingSessionParams],
  );

  const handleDateChange = useCallback(
    (date: string) => {
      setResourceBookingSessionParams({
        date,
      });
    },
    [setResourceBookingSessionParams],
  );

  const handleResourceChange = useCallback(
    (resourceId: string) => {
      if (selectedResource === resourceId) {
        unselectResource();
      } else {
        selectResource(resourceId);
      }
    },
    [selectResource, selectedResource, unselectResource],
  );

  if (!collectionId || !groupId || !listing || !resourceGroup) {
    return <h1>{translate('noResourceGroupFound')}</h1>;
  }

  const priceTiers = sessions
    .flatMap<PriceTier>((e) => (e ? e.priceTiers ?? [] : []))
    .sort((a, b) => a.price - b.price);
  const startingPrice = priceTiers[0]?.price || 0;

  const resourcesWithAvailabilities = resourceGroup.experiences.filter(
    (exp) =>
      !!sessions.find(
        (session) =>
          session.experienceId === exp.id &&
          (!duration || session.duration === duration) &&
          (!time || session.startDateTime.split('T')[1] === time),
      ),
  );

  const resourceGroupPayload = {
    description: resourceGroup.description ?? 'Description unavailable',
    experiences: resourceGroup.experiences,
    groupListItems: resourcesWithAvailabilities.map((item) => ({
      id: item.id,
      name: item.title ?? 'Title unavailable',
      number: item.number ?? '',
    })),
    headline: resourceGroup.description ?? 'Description unavailable',
    headlineTags: listing.vibes.map(({ name }) => name),
    heroImage: resourceGroup.medias,
    id: resourceGroup.id,
    includedItems: resourceGroup.included,
    price: startingPrice,
    title: resourceGroup.title ?? 'Title unavailable',
  };

  if (!listing && !resourceGroup) {
    return <ResourceGroupPageSkeleton />;
  }

  return (
    <ResourceGroupLayout
      availableDates={availableDates}
      brandFlagName={
        eligibleProgram && Object.values(eligibleProgram)[0]?.payEligible
          ? Object.values(eligibleProgram)[0]?.org
          : undefined
      }
      canReserve={!!selectedResource}
      categoryName={listing.category?.name}
      providerBrandId={listing.providerBrandId}
      collectionCustomCTA={listing.customCallToAction ?? null}
      collectionId={listing.resourceGroupCollectionId ?? undefined}
      collectionLocation={listing.location?.name || 'Location unavailable'}
      collectionName={listing.title ?? 'Title unavailable'}
      collectionProductLine={listing.productLine}
      hyattEligible={isHyattEligible(eligibleProgram)}
      isLoadingAvailableDates={isLoadingAvailableDates}
      onDateChange={handleDateChange}
      onMonthChange={setDateForCalendarAvailability}
      onReserveClick={handleReserveClick}
      onResourceChange={handleResourceChange}
      onSessionChange={handleSessionChange}
      resourceGroup={resourceGroupPayload}
      selectedDate={date || ''}
      selectedDuration={duration || 0}
      selectedResourceId={selectedResource}
      selectedResourcePrice={selectedResourcePriceTier?.price}
      selectedTime={time || ''}
      sessionsTimeOptions={allSessionTimes}
    />
  );
};
